import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Check, ListChecks, RefreshCw, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { fuzzyFilter } from "@/lib/fuzzyMatch";
import {
  budgetPushService,
  type BudgetPushJob,
  type PushPreview,
} from "@/services/budgetPushService";

/**
 * The Planner webapp's Budget Push Queue, rehomed (P5). One row per
 * contract+cycle awaiting a Suggested-Daily push to Google Ads; Approve
 * pushes synchronously (the server resolves the approval request first,
 * then mutates campaign budgets through the audited writer).
 */

const STATUS_LABEL: Record<BudgetPushJob["status"], string> = {
  queued: "Awaiting approval",
  approved: "Approved",
  pushing: "Pushing",
  pushed: "Pushed",
  failed: "Failed",
  cancelled: "Cancelled",
  skipped: "Skipped",
};

const STATUS_STYLE: Record<BudgetPushJob["status"], string> = {
  queued: "bg-coral-light text-coral-dark",
  approved: "bg-teal-light text-teal-dark",
  pushing: "bg-teal-light text-teal-dark",
  pushed: "bg-teal-light text-teal-dark",
  failed: "bg-destructive/10 text-destructive",
  cancelled: "bg-muted text-muted-foreground",
  skipped: "bg-muted text-muted-foreground",
};

const TRIGGER_LABEL: Record<BudgetPushJob["trigger_source"], string> = {
  cycle_reset: "Cycle reset",
  routine: "Daily routine",
  manual: "Manual",
  retry: "Retry",
};

const OPEN = ["queued", "approved", "pushing"];
const usd = (n: number | null | undefined) =>
  n == null ? "—" : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function BudgetPushQueueCard() {
  const [jobs, setJobs] = useState<BudgetPushJob[]>([]);
  const [cadence, setCadence] = useState("manual_approval");
  const [openOnly, setOpenOnly] = useState(true);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ id: string; data: PushPreview } | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  const load = useCallback(async () => {
    try {
      const [rows, cad] = await Promise.all([
        budgetPushService.listJobs(openOnly ? OPEN : []),
        budgetPushService.cadence(),
      ]);
      setJobs(rows);
      setCadence(cad.push_cadence);
      setUnavailable(false);
    } catch (err) {
      // 503 = flag off on this environment — render the card as dormant.
      if ((err as { response?: { status?: number } })?.response?.status === 503) {
        setUnavailable(true);
      }
    }
  }, [openOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  // Fuzzy search, best match first ("mke" finds Mike Hammer's jobs;
  // lib/fuzzyMatch). The queue has no user sort, so ranking is safe.
  const filtered = useMemo(
    () =>
      fuzzyFilter(jobs, search, (j) => [j.contract_name, j.trigger_source, j.cap_reason, j.status]),
    [jobs, search],
  );

  const act = async (id: string, fn: () => Promise<unknown>, done: string) => {
    setBusy(id);
    setNote(null);
    try {
      const res = (await fn()) as { status?: string; error?: string } | undefined;
      setNote(
        res?.status === "failed"
          ? `Push failed: ${res.error ?? "unknown error"}`
          : done,
      );
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      setNote(detail || "Action failed.");
    } finally {
      setBusy(null);
      void load();
    }
  };

  if (unavailable) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4 text-sm text-muted-foreground">
          Budget push queue is switched off here
          (PLANNER_BUDGET_PUSH_ENABLED).
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-sm">
      <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/30 px-4 py-3">
        <ListChecks className="h-5 w-5 text-coral" />
        <span className="font-semibold">Budget Push Queue</span>
        <Badge variant="outline" className="font-normal text-muted-foreground">
          Cadence: {cadence.replace(/_/g, " ")}
        </Badge>
        <span className="text-xs text-muted-foreground">
          {jobs.filter((j) => j.status === "queued").length} awaiting approval
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search client / trigger…"
            className="h-8 w-52"
          />
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => setOpenOnly((v) => !v)}
          >
            {openOnly ? "Show all" : "Show open only"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            disabled={busy === "__enqueue"}
            onClick={() =>
              act("__enqueue", () => budgetPushService.enqueue(), "Enqueued the active roster.")
            }
          >
            Enqueue all
          </Button>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => void load()} aria-label="Refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {note ? (
        <div className="border-b border-border px-4 py-2 text-sm text-muted-foreground">{note}</div>
      ) : null}

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/20">
              <TableHead className="min-w-[200px]">Client</TableHead>
              <TableHead className="w-[120px]">Cycle</TableHead>
              <TableHead className="w-[110px] text-right">Daily</TableHead>
              <TableHead className="w-[130px]">Status</TableHead>
              <TableHead className="w-[110px]">Trigger</TableHead>
              <TableHead className="min-w-[160px]">Detail</TableHead>
              <TableHead className="w-[210px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                  No jobs{openOnly ? " open" : ""} — the daily routine enqueues the active roster each morning.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((j) => {
                const daily = j.capped_daily_dollars ?? j.computed_daily_dollars;
                return (
                  <>
                    <TableRow key={j.id}>
                      <TableCell className="font-medium">{j.contract_name}</TableCell>
                      <TableCell className="whitespace-nowrap tabular-nums text-muted-foreground" title={`${j.cycle_start} → ${j.cycle_end}`}>
                        {j.cycle_start}
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">
                        {j.capped_daily_dollars != null ? (
                          <span title={j.cap_reason ?? "Capped"} className="inline-flex items-center gap-1">
                            <AlertTriangle className="h-3.5 w-3.5 text-coral" />
                            {usd(daily)}
                          </span>
                        ) : (
                          usd(daily)
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={cn("rounded px-2 py-0.5 text-xs font-semibold", STATUS_STYLE[j.status])}>
                          {STATUS_LABEL[j.status]}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{TRIGGER_LABEL[j.trigger_source]}</TableCell>
                      <TableCell className="max-w-[240px] truncate text-xs text-muted-foreground" title={j.error ?? j.allocation_reason ?? j.cap_reason ?? ""}>
                        {j.error ?? j.allocation_reason ?? j.cap_reason ?? "—"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8"
                          disabled={busy === j.id}
                          onClick={() =>
                            act(j.id, async () => {
                              const p = await budgetPushService.preview(j.id);
                              setPreview((cur) => (cur?.id === j.id ? null : { id: j.id, data: p }));
                              return undefined;
                            }, "")
                          }
                        >
                          Split
                        </Button>
                        {j.status === "queued" ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 text-teal-dark hover:bg-teal-light"
                            disabled={busy === j.id}
                            onClick={() => {
                              if (window.confirm(`Approve and push ${usd(daily)}/day for ${j.contract_name}? This writes live campaign budgets.`)) {
                                void act(j.id, () => budgetPushService.approve(j.id), "Approved + pushed.");
                              }
                            }}
                          >
                            <Check className="mr-1 h-4 w-4" /> Approve
                          </Button>
                        ) : null}
                        {OPEN.includes(j.status) ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:bg-destructive/10"
                            title="Cancel"
                            aria-label="Cancel job"
                            disabled={busy === j.id}
                            onClick={() => void act(j.id, () => budgetPushService.cancel(j.id), "Cancelled.")}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                    {preview?.id === j.id ? (
                      <TableRow key={`${j.id}-preview`} className="bg-muted/20">
                        <TableCell colSpan={7} className="px-6 py-2">
                          <div className="text-xs text-muted-foreground">
                            Split ({preview.data.kind}
                            {preview.data.reason ? ` — ${preview.data.reason}` : ""}), total {usd(preview.data.effective_daily_total)}/day:
                          </div>
                          <div className="mt-1 flex flex-wrap gap-3 text-xs">
                            {preview.data.campaigns.map((c) => (
                              <span key={c.campaign_id} className="rounded border border-border px-2 py-1">
                                {c.campaign_name ?? c.campaign_id}: {usd(c.current_daily_dollars)} → {usd(c.proposed_daily_dollars)}
                              </span>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
